import { createSelector } from '@reduxjs/toolkit';
import type { AssetListItem, AssetsListResponse } from '@fleet-bffs/api-contract';
import { bffWebApi } from '~/api';
import type { AppState } from '~/reducers';
import type {
  InventoryV2FilterParams,
  InventoryV2FilterValues,
  InventoryV2PageArgs,
} from '../models/inventory-v2';
import { toFilterParams } from '../utils/filter-values';

const ROW_ENDPOINTS = new Set(['getInventoryV2Page', 'getInventoryV2Pins']);
const NO_ROWS: ReadonlyMap<string, AssetListItem> = new Map();

const selectQueries = (state: AppState) => state[bffWebApi.reducerPath].queries;

const selectSelectedIds = createSelector(
  [
    (state: AppState) => state.inventoryV2Selection.toggled,
    (state: AppState) => state.inventoryV2Selection.seeds,
  ],
  (toggled, seeds): ReadonlySet<string> =>
    new Set([...toggled.map((asset) => asset.assetDetails.id), ...seeds.map((seed) => seed.iprId)])
);

const selectListFilters = createSelector(
  [(state: AppState) => state.assetListFilter.filtersValues],
  (values) => toFilterParams(values as InventoryV2FilterValues)
);

const sameFilters = (a: InventoryV2FilterParams, b: InventoryV2FilterParams) =>
  JSON.stringify(a) === JSON.stringify(b);

const sameRows = (a: ReadonlyMap<string, AssetListItem>, b: ReadonlyMap<string, AssetListItem>) =>
  a.size === b.size && [...a].every(([id, row]) => b.get(id) === row);

/**
 * The rows of the selected assets (picked rows and map seeds) that the paged list holds in its
 * cached pages and pinned rows for the current filters, by asset id. Live updates patch these
 * entries, so the rows stay current. Returns the same map while those rows are unchanged.
 */
export const selectLoadedSelectedRows = createSelector(
  [selectQueries, selectSelectedIds, selectListFilters],
  (queries, ids, filters): ReadonlyMap<string, AssetListItem> => {
    if (ids.size === 0) {
      return NO_ROWS;
    }
    const rows = new Map<string, AssetListItem>();
    for (const entry of Object.values(queries)) {
      if (
        entry?.endpointName !== undefined &&
        ROW_ENDPOINTS.has(entry.endpointName) &&
        entry.data &&
        sameFilters((entry.originalArgs as InventoryV2PageArgs | undefined)?.filters ?? {}, filters)
      ) {
        for (const asset of (entry.data as AssetsListResponse).machines) {
          if (ids.has(asset.assetDetails.id)) {
            rows.set(asset.assetDetails.id, asset);
          }
        }
      }
    }
    return rows.size === 0 ? NO_ROWS : rows;
  },
  { memoizeOptions: { resultEqualityCheck: sameRows } }
);
